/**
 * Comment Service
 * 
 * Handles loading and persisting comments to/from backend
 * 
 * Requirements: 5.4
 */

import { getApiBaseUrl } from '../config';
import type { CommentMarker } from '../store/daw-store';

/**
 * Load comments for a project from backend
 * 
 * Requirements: 5.4, 15.4 (lazy-load comment text)
 * 
 * @param projectId - Project ID
 * @param lazyLoad - If true, loads only metadata without text (Requirement 15.4)
 */
export async function loadCommentsFromBackend(
  projectId: string,
  options?: { lazyLoad?: boolean }
): Promise<CommentMarker[]> {
  try {
    const apiBase = getApiBaseUrl();
    const lazyLoad = options?.lazyLoad ?? false;
    
    // Add query parameter for lazy loading (Requirement 15.4)
    const url = lazyLoad
      ? `${apiBase}/api/projects/${projectId}/comments?metadata_only=true`
      : `${apiBase}/api/projects/${projectId}/comments`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to load comments: ${response.statusText}`);
    }
    
    const data = await response.json();
    const comments = data.comments || [];
    
    // If lazy loading, set text to empty string as placeholder
    if (lazyLoad) {
      return comments.map((comment: CommentMarker) => ({
        ...comment,
        text: '', // Placeholder, will be loaded on demand
        _textLoaded: false, // Internal flag to track if text is loaded
      }));
    }
    
    return comments;
  } catch (error) {
    console.error('Failed to load comments from backend:', error);
    // Return empty array on error to allow app to continue
    return [];
  }
}

/**
 * Load comment text on demand (Requirement 15.4)
 * 
 * This is used when comments are initially loaded with lazy loading.
 * Call this when user clicks on a comment to view its full text.
 */
export async function loadCommentText(
  projectId: string,
  commentId: string
): Promise<string> {
  try {
    const apiBase = getApiBaseUrl();
    const response = await fetch(
      `${apiBase}/api/projects/${projectId}/comments/${commentId}/text`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to load comment text: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.text || '';
  } catch (error) {
    console.error('Failed to load comment text:', error);
    return '';
  }
}

/**
 * Save comment to backend
 * 
 * This is called automatically by the WebSocket sync, but can also be used
 * as a fallback if WebSocket is not available
 */
export async function saveCommentToBackend(
  projectId: string,
  comment: CommentMarker
): Promise<void> {
  try {
    const apiBase = getApiBaseUrl();
    const response = await fetch(`${apiBase}/api/projects/${projectId}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ comment }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to save comment: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Failed to save comment to backend:', error);
    throw error;
  }
}

/**
 * Update comment in backend
 */
export async function updateCommentInBackend(
  projectId: string,
  commentId: string,
  updates: Partial<CommentMarker>
): Promise<void> {
  try {
    const apiBase = getApiBaseUrl();
    const response = await fetch(
      `${apiBase}/api/projects/${projectId}/comments/${commentId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to update comment: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Failed to update comment in backend:', error);
    throw error;
  }
}

/**
 * Delete comment from backend
 */
export async function deleteCommentFromBackend(
  projectId: string,
  commentId: string
): Promise<void> {
  try {
    const apiBase = getApiBaseUrl();
    const response = await fetch(
      `${apiBase}/api/projects/${projectId}/comments/${commentId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to delete comment: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Failed to delete comment from backend:', error);
    throw error;
  }
}
